import { ThemeUIStyleObject } from 'theme-ui';

import * as dto from 'client/dto';
import theme from 'theme';
import { gridStyleNoPadding } from 'utils/cssHelpers';

import { VariantLiteral } from 'components/_common/Typography/Typography';
import { LocationSearchResult, LocationSearchType } from 'components/_controls/AoiSearch/models';

import {
  ConnectivityGroupByOptions,
  ConnectivityLocationFormat,
  ModSortOption,
  MopSortOption,
} from './connectivity.models';

export const mopOriginGridCols = ['1 / span all', null, '1 / span 3'];
export const mopDestinationGridCols = ['1 / span all', null, '4 / span 3'];
export const mopGroundTravelGridCols = ['1 / span all', null, '7 / span 3'];
export const mopAirTravelGridCols = ['1 / span all', null, '10 / span 3'];
export const mopAirTravelGridColsNoGround = ['1 / span all', null, '7 / span 6'];

export const modOriginColGridCols = ['1 / span all', '1 / span 3', '1 / span 3', '1 / span 2'];
export const modDestinationColGridCols = ['1 / span all', '4 / span 3', '4 / span 3', '3 / span 2'];
export const modRiskColGridCols = ['1 / span all', '7 / span 3', '7  / span 2', '5 / span 2'];
export const modValueColGridCols = ['1 / span all', null, '9 / span 4', '7 / span 6'];

export const AggTitleVariant: VariantLiteral[] = ['caption', 'overline'];
export const AggValueVariant: VariantLiteral[] = ['body-large-emphasized', 'body-small-emphasized'];
export const AggValueFontColor = 'stone90';
export const AggTitleFontColor = 'stone70';
export const AggRowStyles: ThemeUIStyleObject = {
  bg: theme.colors.deepSea10,
  gridColumn: '1 / span all',
  borderBottom: `1px solid ${theme.colors.stone20}`,
  borderTop: `1px solid ${theme.colors.stone20}`,
  '& .tr-content': {
    gridColumn: '1 / span all',
    ...gridStyleNoPadding,
  },
};

export const getTravelLocationModelDisplayName = (
  model: dto.TravelLocationModel,
  activeGroupBy: dto.ConnectivityGroupByType
): ConnectivityLocationFormat => {
  const { locationName, city, province, country, locationType, locationCode } = model;

  switch (activeGroupBy) {
    case dto.ConnectivityGroupByType.Country:
      return { highlightText: locationName };

    case dto.ConnectivityGroupByType.Province:
      if (locationType === dto.LocationType.Country)
        return { highlightText: country, grayedText: '(Country)' };

      return { highlightText: province, appendText: country };

    case dto.ConnectivityGroupByType.County:
      if (locationType === dto.LocationType.Country)
        return { highlightText: country, grayedText: '(Country)' };

      if (locationType === dto.LocationType.Province)
        return { highlightText: province, appendText: country, grayedText: '(State/Province)' };

      return { highlightText: locationName, appendText: `${province}, ${country}` };
    case dto.ConnectivityGroupByType.Airport:
    default:
      return {
        highlightText: `${locationName} (${locationCode})`,
        appendText: `${city?.length ? `${city}, ` : ''}${country}`,
      };
  }
};

export const doesODLocationPairBubble = (
  origin: dto.TravelLocationModel,
  originGroupBy: dto.ConnectivityGroupByType,
  destination: dto.TravelLocationModel,
  destinationGroupBy: dto.ConnectivityGroupByType
) =>
  getTravelLocationModelDisplayName(origin, originGroupBy)?.grayedText?.length > 0 ||
  getTravelLocationModelDisplayName(destination, destinationGroupBy)?.grayedText?.length > 0;

export const getDisplayNameFromLocationSearchResult = (model: LocationSearchResult) => {
  if (model == null) return { highlightText: 'Anywhere' };

  const { name, countryName, cityName, provinceName, airportCode, locationType } = model;

  switch (locationType) {
    case dto.LocationType.Country:
      return { highlightText: name };

    case dto.LocationType.Province:
      return { highlightText: provinceName, appendText: countryName };

    case dto.LocationType.City:
      return { highlightText: cityName, appendText: `${provinceName}, ${countryName}` };

    case dto.LocationType.MetroArea:
      return { highlightText: name, appendText: `${provinceName}, ${countryName}` };

    case dto.LocationType.County:
      return { highlightText: name, appendText: `${provinceName}, ${countryName}` };

    default:
      return {
        highlightText: `${name} (${airportCode})`,
        appendText: `${cityName?.length ? `${cityName}, ` : ''}${countryName}`,
      };
  }
};

/**
 * Gets the group by types for a location based on it's location type
 * Defined here: https://bluedotglobal.atlassian.net/browse/PT-3979
 */
export const getConnectivityGroupByTypes = (
  location: LocationSearchResult,
  oppositeLocation: LocationSearchResult,
  domesticIncluded: boolean
) => {
  // TODO: df248ed6 The domesticIncluded flag gets removed when support for counties outside of the US is added
  const hasCounties =
    location != null
      ? location.hasCounties
      : (oppositeLocation?.hasCounties && domesticIncluded) || !oppositeLocation?.hasCounties;
  const hasProvinces = location != null ? location.hasProvinces : true;

  // The airport is the lowest option so it will always be just airport
  if (location?.locationType === LocationSearchType.Airport)
    return [
      ConnectivityGroupByOptions.find(opt => opt.value === dto.ConnectivityGroupByType.Airport),
    ];

  const exclusions = new Set<dto.ConnectivityGroupByType>();
  const isNullOrCountry =
    location?.locationType == null || location?.locationType === LocationSearchType.Country;

  if (location?.locationType !== LocationSearchType.County && !hasCounties)
    exclusions.add(dto.ConnectivityGroupByType.County);

  if (location?.locationType !== LocationSearchType.Province && (!hasProvinces || !isNullOrCountry))
    exclusions.add(dto.ConnectivityGroupByType.Province);

  if (!isNullOrCountry) exclusions.add(dto.ConnectivityGroupByType.Country);

  return ConnectivityGroupByOptions.filter(opt => !exclusions.has(opt.value));
};

export const mapMopSortOptions = (options: MopSortOption[]) =>
  options.map(opt => ({
    text: opt.shortText,
    value: opt.sortType,
  }));

export const mapModSortOptions = (options: ModSortOption[]) =>
  options.map(opt => ({
    text: opt.shortText,
    value: opt.sortType,
  }));

export const getModDisabledToggleText = (isDomesticOnly: boolean) =>
  isDomesticOnly
    ? 'Your search query only shows domestic flights.'
    : 'Your search query does not include domestic flights.';

export const getDisabledMopDomesticToggleText = (
  isDomesticAllowed: boolean,
  isDomesticOnly: boolean
) =>
  isDomesticOnly
    ? 'Only domestic data is available based on your search query.'
    : !isDomesticAllowed
    ? 'Domestic data is unavailable based on your search query.'
    : undefined;
